import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import UILink from 'UIComponents/link/UILink';
import UIAlert from 'UIComponents/alert/UIAlert';
import { MODAL_WIDTH, RequestStatus } from '../../Constants';
import FileVisibilityLearnMoreLink from '../FileVisibilityLearnMoreLink';
import { FileVisibilityOptionNames } from '../../enums/FileVisibilityOptions';
import FileVisibilityConfirmButton from '../FileVisibilityConfirmButton';
import getConfirmFileVisibilityI18nLabel from '../../utils/getConfirmFileVisibilityI18nLabel';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getVisibilityOptionI18nKey(visibilityOption, suffix) {
  return `FileManagerCore.changeFileVisibilityConfirmationModals.${visibilityOption}.${suffix}`;
}
function getI18nKey(suffix) {
  return getVisibilityOptionI18nKey('shared', suffix);
}
const propTypes = {
  handleConfirm: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchNestedCount: PropTypes.func.isRequired,
  selectedFileVisibilitySetting: PropTypes.oneOf(Object.keys(FileVisibilityOptionNames)).isRequired,
  totalNestedFileCount: PropTypes.number,
  changeFileVisibilityRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired,
  totalNestedFileCountRequestStatus: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired,
  isFromPickerChangeVisibility: PropTypes.bool.isRequired
};
export default class ChangeFileVisibilityConfirmationModal extends Component {
  constructor(...args) {
    super(...args);
    this.renderMessageWithUndefinedFileCount = originalI18nKeyPath => {
      const {
        fetchNestedCount,
        totalNestedFileCountRequestStatus
      } = this.props;
      const i18nKeyPath = `${originalI18nKeyPath}_jsx`;
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKeyPath,
        options: {
          loading: totalNestedFileCountRequestStatus === RequestStatus.PENDING,
          onTryAgain: fetchNestedCount
        },
        elements: {
          Link: UILink,
          Strong: 'b'
        }
      });
    };
    this.renderVisibleToAllModalBody = () => {
      const {
        totalNestedFileCount
      } = this.props;
      return /*#__PURE__*/_jsxs("div", {
        className: "m-top-3",
        children: [totalNestedFileCount !== undefined ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: getVisibilityOptionI18nKey('visibleToAll', 'modalBody'),
          options: {
            count: totalNestedFileCount
          }
        }) : this.renderMessageWithUndefinedFileCount(getVisibilityOptionI18nKey('visibleToAll', 'modalBody')), /*#__PURE__*/_jsxs("ul", {
          children: [/*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('visibleToAll', 'changesToFiles.item1')
            })
          }), /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('visibleToAll', 'changesToFiles.item2')
            })
          }), /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('visibleToAll', 'changesToFiles.item3')
            })
          })]
        })]
      });
    };
    this.renderHiddenFromAllModalBody = () => {
      const {
        totalNestedFileCount
      } = this.props;
      return /*#__PURE__*/_jsxs("div", {
        className: "m-top-3",
        children: [totalNestedFileCount !== undefined ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: getVisibilityOptionI18nKey('hiddenFromAllModal', 'modalBody'),
          options: {
            count: totalNestedFileCount
          }
        }) : this.renderMessageWithUndefinedFileCount(getVisibilityOptionI18nKey('hiddenFromAllModal', 'modalBody')), /*#__PURE__*/_jsxs("ul", {
          children: [/*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('hiddenFromAllModal', 'changesToFiles.item1')
            })
          }), /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('hiddenFromAllModal', 'changesToFiles.item2')
            })
          }), /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getVisibilityOptionI18nKey('hiddenFromAllModal', 'changesToFiles.item3')
            })
          })]
        })]
      });
    };
    this.renderVisibleToUsersOnlyModalBody = () => {
      const {
        totalNestedFileCount
      } = this.props;
      return /*#__PURE__*/_jsx("div", {
        className: "m-top-3",
        children: totalNestedFileCount !== undefined ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: getVisibilityOptionI18nKey('visibleToUsersOnly', 'modalBody'),
          options: {
            count: totalNestedFileCount
          }
        }) : this.renderMessageWithUndefinedFileCount(getVisibilityOptionI18nKey('visibleToUsersOnly', 'modalBody'))
      });
    };
  }
  renderModalBody(selectedFileVisibilitySetting) {
    switch (selectedFileVisibilitySetting) {
      case FileVisibilityOptionNames.VISIBLE_TO_ALL:
        return this.renderVisibleToAllModalBody();
      case FileVisibilityOptionNames.VISIBLE_TO_USERS_HIDDEN_FROM_SEARCH_ENGINES:
        return this.renderVisibleToUsersOnlyModalBody();
      case FileVisibilityOptionNames.HIDDEN_FROM_ALL:
        return this.renderHiddenFromAllModalBody();
      default:
        return null;
    }
  }
  renderModalDescription() {
    const {
      selectedFileVisibilitySetting,
      isFromPickerChangeVisibility
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.renderModalBody(selectedFileVisibilitySetting), /*#__PURE__*/_jsx(FileVisibilityLearnMoreLink, {}), isFromPickerChangeVisibility && /*#__PURE__*/_jsx(UIAlert, {
        className: "m-top-3",
        type: "info",
        use: "inline",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: getI18nKey('refreshPickerTip')
        })
      })]
    });
  }
  render() {
    const {
      handleConfirm,
      handleClose,
      selectedFileVisibilitySetting,
      changeFileVisibilityRequestStatus
    } = this.props;
    return /*#__PURE__*/_jsx(UIConfirmModal, {
      use: "danger",
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-id": "make-file-private-modal",
        message: getI18nKey(selectedFileVisibilitySetting === FileVisibilityOptionNames.VISIBLE_TO_ALL ? 'tunOnTitle' : 'turnOffTitle')
      }),
      onConfirm: handleConfirm,
      confirmUse: "danger",
      onReject: handleClose,
      ConfirmButton: FileVisibilityConfirmButton,
      confirmButtonProps: {
        onClick: () => handleConfirm(selectedFileVisibilitySetting),
        changeFileVisibilityRequestStatus,
        buttonLabelI18nKey: getConfirmFileVisibilityI18nLabel(selectedFileVisibilitySetting)
      },
      rejectButtonProps: {
        disabled: changeFileVisibilityRequestStatus === RequestStatus.PENDING
      },
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: getI18nKey('cancel')
      }),
      width: MODAL_WIDTH,
      description: this.renderModalDescription()
    });
  }
}
ChangeFileVisibilityConfirmationModal.propTypes = propTypes;